import DialogViewPrompt from "@/components/prompt/dialog-view-prompt";
import { useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { ActivateUserTacticToolOutput, PromptDTO } from "@shaarp/common";
import { ToolUIPart } from "ai";
import { CrosshairIcon, Loader2Icon } from "lucide-react";
import { useState } from "react";

/**
 * The tool returns either the activated tactic or a plain-language sentence
 * when the model asked for a slug it may not use. Only the structured shape
 * marks the thread; the sentence is left to the model's prose.
 */
function isActivatedTactic(
  output: unknown
): output is ActivateUserTacticToolOutput {
  // Every field of the type, not the two this card reads: the guard is what
  // tells TypeScript the value is one, so a partial check hands the next reader
  // an `undefined` the compiler swears is a string.
  return (
    !!output &&
    typeof output === "object" &&
    typeof (output as ActivateUserTacticToolOutput).title === "string" &&
    typeof (output as ActivateUserTacticToolOutput).slug === "string" &&
    typeof (output as ActivateUserTacticToolOutput).promptId === "string"
  );
}

type ChatToolActivateTacticProps = {
  readonly state: ToolUIPart["state"];
  readonly output: unknown;
};

/**
 * Names the tactic the AI applied, on one compact line, and opens it on click.
 *
 * The body is not in the thread and not in the tool result: it is an
 * instruction the model follows, stripped before the turn is persisted. So the
 * card fetches the tactic when the reader asks for it, rather than carrying a
 * copy that would go stale the next time its author edits it.
 *
 * Renders nothing until the call is final: the part arrives mid-stream with no
 * output, and can end as `output-error`.
 */
export default function ChatToolActivateTactic({
  state,
  output,
}: ChatToolActivateTacticProps) {
  const { t } = useTranslation();
  const api = useApi();
  const toastError = useToastError();

  const [prompt, setPrompt] = useState<PromptDTO | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  if (state !== "output-available" || !isActivatedTactic(output)) {
    return null;
  }

  const onOpen = async () => {
    // Guarded rather than queued: a second click while the first is in flight
    // would open two dialogs over one another.
    if (isLoading) {
      return;
    }
    try {
      setIsLoading(true);
      const { data } = await api.get(`/prompt/${output.promptId}`);
      setPrompt(data);
    } catch {
      // A tactic deleted since this answer was written, or one this reader may
      // no longer open, is the ordinary case here — not a bug worth a dialog
      // with nothing in it.
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        disabled={isLoading}
        className="mb-2 flex w-full items-center gap-2 rounded-xl border bg-background p-3 text-left not-prose transition-colors hover:bg-muted/50 disabled:cursor-progress"
      >
        {isLoading ? (
          <Loader2Icon className="size-4 shrink-0 animate-spin text-primary" />
        ) : (
          <CrosshairIcon className="size-4 shrink-0 text-primary" />
        )}
        <span className="text-sm text-muted-foreground">
          {t("chat.message.tool.activateUserTactic")}
        </span>
        <span className="truncate text-sm font-medium">{output.title}</span>
      </button>
      {prompt && (
        <DialogViewPrompt prompt={prompt} onClose={() => setPrompt(null)} />
      )}
    </>
  );
}
